import { Injectable, signal, WritableSignal, computed, inject } from '@angular/core';
import { Client } from 'typesense';
import { environment } from '../../environments/environment';
import { ProductResume } from '../product/product.types';
import { LocalStorageService } from './local-storage.service';

const RECENT_SEARCHES_STORAGE_KEY = 'navego_recent_searches';
const RECENT_SEARCHES_LIMIT = 5;

/**
 * Flat document stored in Typesense. Fields are indexed for search/filter/facet/sort.
 * `product_data` contains the full product as a JSON string (not indexed).
 */
export interface TypesenseDocument {
  id: string;
  name: string;
  description_short?: string;
  producer_name?: string;
  producer_id?: string;
  category_id: string;
  category_name: string;
  subcategory_id: string;
  subcategory_name: string;
  tag_ids?: string[];
  tag_names?: string[];
  is_subscription: boolean;
  is_a_la_carte: boolean;
  is_on_sale?: boolean;
  price: number;
  original_price?: number;
  position?: number;
  product_url: string;
  img: string;
  product_data?: string;
}

export interface StockInfoEntry {
  productId: number;
  attribute?: { id: number; value: { id: number } };
  variantId: number;
  packageId?: number;
  stock: number;
  limit: number;        // negative buffer; limit === 0 means strict
  hasNotify?: boolean;
}

/**
 * Rich product structure parsed from `product_data` JSON field.
 * Contains nested objects needed for frontend rendering.
 */
export interface TypesenseProduct {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  /**
   * Preformatted price range (e.g. "$25-27") for bundles whose weekly contents have not
   * been published yet, so they carry no final price. Indexed flat inside product_data;
   * the shop card gets the same value from `bundle.priceRange`.
   */
  priceRange?: string;
  productUrl: string;
  img: string;
  isSubscription: boolean;
  isALaCarte: boolean;
  isBundle?: boolean;
  category: { id: number; name: string };
  subcategory: { id: number; name: string };
  subcategorySon?: { id: number; name: string };
  producer?: { id: number; name: string };
  tags?: { id: number; name: string; icon: string }[];
  attributes?: any[];
  packages?: any[];
  description?: { short: string; long: string };
  display?: string;
  stockInfo?: StockInfoEntry[];
}

const LOW_STOCK_THRESHOLD = 9;

export interface SearchSuggestion {
  id: string;
  name: string;
  type: 'product' | 'category' | 'producer';
  img?: string;
  productUrl?: string;
  highlight?: string;
  price?: number;
  originalPrice?: number;
  /** See TypesenseProduct.priceRange — only meaningful for bundles with no final price. */
  priceRange?: string;
  producerName?: string;
  isSubscription?: boolean;
  isALaCarte?: boolean;
  isBundle?: boolean;
  packages?: any[];
  attributes?: any[];
  canQuickAdd?: boolean;
  variantId?: number;
  packageId?: number;
  /**
   * Units the selected package puts in the order per add ("8 count" → 8). Drives both the
   * stock maths and the quick-add guard, which must reserve the whole package, not one unit.
   */
  packageQuantity?: number;
  attributeId?: number;
  attributeValueId?: number;
  parentProductId?: string;
  variantLabel?: string;
  /** Raw stockInfo from backend; used during expansion, stripped from final cards. */
  stockInfo?: StockInfoEntry[];
  /** Derived per-variant stock state attached to each final card. */
  stock?: { available: number; isOut: boolean; isLow: boolean };
}

export interface SearchFacet {
  field: string;
  counts: { value: string; count: number }[];
}

export interface SearchResult {
  products: ProductResume[];
  totalFound: number;
  facets: SearchFacet[];
  searchTime: number;
}

export function isSimpleOneTimeProduct(product: {
  isALaCarte?: boolean;
  isSubscription?: boolean;
  isBundle?: boolean;
  attributes?: any[];
  packages?: any[];
}): boolean {
  return !!(
    product.isALaCarte &&
    !product.isBundle &&
    !product.attributes?.length &&
    (product.packages?.length ?? 0) <= 1
  );
}

export function evaluateStockForSuggestion(suggestion: SearchSuggestion): { available: number; isOut: boolean; isLow: boolean } | undefined {
  // A bundle's own stock row is meaningless: the shop card bails out of per-variant stock
  // validation for anything carrying bundle items (product-card-v2.component.ts:708) and
  // derives availability from the constituent items instead, gated on the product's
  // checkStock flag. Indexed bundles always carry stock 0 / limit 0, so honouring that row
  // here marked perfectly available bundles as "Out of stock" while the shop showed them
  // as buyable.
  if (suggestion.isBundle) return undefined;

  const stockInfo = suggestion.stockInfo;
  if (!stockInfo?.length) return undefined;

  const parentId = parseInt(suggestion.parentProductId || suggestion.id, 10);
  const entry = stockInfo.find(e => {
    if (e.productId !== parentId) return false;
    if (suggestion.attributeId && suggestion.attributeValueId) {
      return e.attribute?.id === suggestion.attributeId
        && e.attribute?.value?.id === suggestion.attributeValueId;
    }
    return !e.attribute;
  });

  if (!entry) return undefined;

  // One add of a package card reserves the whole package ("8 count" → 8 units), so that is
  // what has to fit in stock — not a single unit. The shop card applies the same multiplier
  // before deciding it is out of stock (product-card-v2.component.ts:715) and drops packages
  // that no longer fit from its selector entirely (:1011). Without it, a large package
  // sitting on a small remainder rendered as perfectly buyable here.
  const unitsPerAdd = suggestion.packageQuantity && suggestion.packageQuantity > 0
    ? suggestion.packageQuantity
    : 1;

  const newStockAfterQty = entry.stock - unitsPerAdd;
  const isOut = newStockAfterQty < 0
    ? (entry.limit === 0 || newStockAfterQty < entry.limit)
    : false;

  const availableUnits = entry.limit === 0
    ? Math.max(0, entry.stock)
    : Math.max(0, entry.stock - entry.limit);
  // Counted in whole packages, so "Only N left" measures what the card actually adds.
  // Floor because a partial package cannot be bought (3 units left of a "2 count" → 1).
  const available = Math.floor(availableUnits / unitsPerAdd);
  const isLow = !isOut && available > 0 && available < LOW_STOCK_THRESHOLD;

  return { available, isOut, isLow };
}

export function attachStockState(suggestions: SearchSuggestion[]): SearchSuggestion[] {
  return suggestions.map(s => {
    const stock = evaluateStockForSuggestion(s);
    const { stockInfo: _drop, ...rest } = s;
    return stock ? { ...rest, stock } : rest;
  });
}

/**
 * A package's indexed price is per unit, so its real price is price × quantity — the same
 * arithmetic the shop card applies (product-card-v2.component.ts:784) and quick-add repeats
 * (search-bar.component.ts:519). Rounded to cents because package quantities are not always
 * whole ("1/2 lb" is 0.5, "4 oz" is 0.25): 3.99 × 0.5 would otherwise reach the card as
 * 1.995. Math.round to cents matches what `| currency` renders on the shop card for every
 * fractional package in the index, so both surfaces show the same amount.
 */
function packagePrice(unitPrice: number, quantity: number): number {
  return Math.round(unitPrice * quantity * 100) / 100;
}

export function expandSuggestions(suggestions: SearchSuggestion[]): SearchSuggestion[] {
  const expanded: SearchSuggestion[] = [];

  for (const suggestion of suggestions) {
    const isSubscriptionOnly = suggestion.isSubscription && !suggestion.isALaCarte;
    if (isSubscriptionOnly || suggestion.isBundle) {
      expanded.push(suggestion);
      continue;
    }

    // Expand by attributes (takes priority over packages)
    if (suggestion.attributes?.length) {
      for (const attribute of suggestion.attributes) {
        const values = attribute.values || [];
        for (const value of values) {
          if (!value.variantId) continue;
          expanded.push({
            ...suggestion,
            id: `${suggestion.id}_attr_${value.variantId}`,
            parentProductId: suggestion.id,
            name: `${suggestion.name} - ${value.name}`,
            img: value.image || suggestion.img,
            price: value.price ?? suggestion.price,
            originalPrice: value.originalPrice ?? suggestion.originalPrice,
            variantId: value.variantId,
            attributeId: attribute.id,
            attributeValueId: value.id,
            variantLabel: value.name,
            canQuickAdd: !!suggestion.isALaCarte && !suggestion.isBundle,
            attributes: [],
            packages: [],
          });
        }
      }
      continue;
    }

    // Expand by packages — single-package products included, so the packageId
    // always travels with the suggestion (ELIO-6622: quick-add shipped no package)
    if (suggestion.packages?.length) {
      for (const pkg of suggestion.packages) {
        // The card takes both the price and the discount from the selected package alone —
        // `variant.originalPrice = pack.originalPrice * pack.quantity` and
        // `variant.hasDiscount = pack.originalPrice` — so a package with no originalPrice
        // carries no strike-through, rather than falling back to the parent's. Guarded on
        // `typeof quantity` and not the card's `pack?.quantity >= 0`, which is also true for
        // a null quantity and would price the package at $0.00.
        const packQty = typeof pkg.quantity === 'number' ? pkg.quantity : null;
        expanded.push({
          ...suggestion,
          id: `${suggestion.id}_pkg_${pkg.id}`,
          parentProductId: suggestion.id,
          name: `${suggestion.name} - ${pkg.name}`,
          packageId: +pkg.id,
          packageQuantity: packQty ?? undefined,
          price: packQty !== null && pkg.price != null
            ? packagePrice(pkg.price, packQty)
            : pkg.price ?? suggestion.price,
          originalPrice: packQty !== null
            ? (pkg.originalPrice ? packagePrice(pkg.originalPrice, packQty) : undefined)
            : pkg.originalPrice ?? suggestion.originalPrice,
          variantLabel: pkg.name,
          canQuickAdd: !!suggestion.isALaCarte && !suggestion.isBundle,
          attributes: [],
          packages: [],
        });
      }
      continue;
    }

    // Simple product — pass through
    expanded.push(suggestion);
  }

  return expanded;
}

@Injectable({
  providedIn: 'root'
})
export class TypesenseSearchService {
  private client: Client;
  private collection: string;
  private querySuggestionsCollection: string;

  private localStorage = inject(LocalStorageService);

  // Signals for reactive state management
  searchQuery: WritableSignal<string> = signal('');
  searchResults: WritableSignal<SearchResult | null> = signal(null);
  suggestions: WritableSignal<SearchSuggestion[]> = signal([]);
  /**
   * How many documents the last autocomplete query matched in total, and how many of them
   * actually came back under `per_page`. Both are raw document counts on purpose:
   * `suggestions()` is post-expansion, so one document can become several variant cards and
   * its length can exceed the number of documents shown. Comparing these two is the only
   * reliable way to answer "are there more results than the dropdown is showing?".
   */
  suggestionsTotalFound: WritableSignal<number> = signal(0);
  suggestionsReturnedCount: WritableSignal<number> = signal(0);
  popularProducts: WritableSignal<SearchSuggestion[]> = signal([]);
  popularSearches: WritableSignal<string[]> = signal([]);
  querySuggestions: WritableSignal<string[]> = signal([]);
  // Persisted to localStorage; hydrated lazily via loadRecentSearches().
  recentSearches: WritableSignal<string[]> = signal([]);
  isSearching: WritableSignal<boolean> = signal(false);
  searchError: WritableSignal<string | null> = signal(null);

  // Computed signals
  hasResults = computed(() => {
    const results = this.searchResults();
    return results !== null && results.totalFound > 0;
  });

  hasSuggestions = computed(() => this.suggestions().length > 0);

  constructor() {
    const typesenseConfig = environment.apis.typesense;

    this.client = new Client({
      nodes: [{
        host: typesenseConfig.host,
        port: typesenseConfig.port,
        protocol: typesenseConfig.protocol
      }],
      apiKey: typesenseConfig.apiKey,
      connectionTimeoutSeconds: 8
    });

    this.collection = typesenseConfig.collection;
    this.querySuggestionsCollection = typesenseConfig.querySuggestionsCollection;
  }

  async getAutocompleteSuggestions(query: string, limit: number = 6): Promise<SearchSuggestion[]> {
    if (!query || query.length < 2) {
      this.suggestions.set([]);
      this.suggestionsTotalFound.set(0);
      this.suggestionsReturnedCount.set(0);
      return [];
    }

    try {
      const searchParameters = {
        q: query,
        query_by: 'name,producer_name,category_name,subcategory_name,description_short',
        query_by_weights: '20,4,2,2,1',
        prioritize_token_position: true,
        prefix: 'true',
        num_typos: '1',
        per_page: limit,
        highlight_full_fields: 'name',
        highlight_start_tag: '<mark>',
        highlight_end_tag: '</mark>'
      };

      const result = await this.client
        .collections(this.collection)
        .documents()
        .search(searchParameters);

      const suggestions: SearchSuggestion[] = (result.hits || []).map((hit: any) => {
        const doc = hit.document as TypesenseDocument;
        const highlight = hit.highlights?.find((h: any) => h.field === 'name');
        const richProduct = this.parseProductData(doc);

        return {
          id: doc.id,
          name: doc.name,
          type: 'product' as const,
          img: doc.img,
          productUrl: doc.product_url,
          highlight: highlight?.snippet || doc.name,
          price: doc.price,
          originalPrice: doc.original_price,
          producerName: doc.producer_name,
          isSubscription: doc.is_subscription,
          isALaCarte: doc.is_a_la_carte,
          isBundle: richProduct?.isBundle ?? false,
          priceRange: richProduct?.priceRange,
          packages: richProduct?.packages,
          attributes: richProduct?.attributes,
          canQuickAdd: richProduct
            ? isSimpleOneTimeProduct(richProduct)
            : isSimpleOneTimeProduct({
                isALaCarte: doc.is_a_la_carte,
                isSubscription: doc.is_subscription,
                isBundle: false,
                attributes: [],
                packages: []
              }),
          stockInfo: richProduct?.stockInfo
        };
      });

      // Generate query suggestions from product names
      const querySuggestions = this.generateQuerySuggestions(query, result.hits || []);
      this.querySuggestions.set(querySuggestions);

      this.suggestionsTotalFound.set(result.found ?? 0);
      this.suggestionsReturnedCount.set((result.hits || []).length);

      const expanded = attachStockState(expandSuggestions(suggestions));
      const reranked = this.rerankByNameMatch(expanded, query);
      this.suggestions.set(reranked);
      return reranked;
    } catch (error) {
      this.suggestions.set([]);
      this.suggestionsTotalFound.set(0);
      this.suggestionsReturnedCount.set(0);
      return [];
    }
  }

  async search(
    query: string,
    options: {
      page?: number;
      perPage?: number;
      filters?: string;
      sortBy?: string;
      facetBy?: string[];
    } = {}
  ): Promise<SearchResult> {
    const {
      page = 1,
      perPage = 20,
      filters = '',
      sortBy = '',
      facetBy = ['category_name', 'subcategory_name', 'producer_name', 'tag_names', 'is_on_sale']
    } = options;

    this.isSearching.set(true);
    this.searchError.set(null);
    this.searchQuery.set(query);

    try {
      const searchParameters: Record<string, string | number | boolean> = {
        q: query || '*',
        query_by: 'name,description_short,producer_name,tag_names,subcategory_name,category_name',
        query_by_weights: '20,2,4,2,2,1',
        prioritize_token_position: true,
        prefix: 'true',
        num_typos: '2',
        typo_tokens_threshold: '1',
        page: String(page),
        per_page: String(perPage),
        highlight_full_fields: 'name',
        highlight_start_tag: '<mark>',
        highlight_end_tag: '</mark>',
        facet_by: facetBy.join(',')
      };

      if (filters) {
        searchParameters['filter_by'] = filters;
      }

      if (sortBy) {
        searchParameters['sort_by'] = sortBy;
      }

      const result = await this.client
        .collections(this.collection)
        .documents()
        .search(searchParameters);

      const transformed = this.transformToProductResume(result.hits || []);
      const products = this.rerankByNameMatch(transformed, query);
      const facets = this.transformFacets(result.facet_counts || []);

      const searchResult: SearchResult = {
        products,
        totalFound: result.found || 0,
        facets,
        searchTime: result.search_time_ms || 0
      };

      this.searchResults.set(searchResult);
      this.isSearching.set(false);

      return searchResult;
    } catch (error: any) {
      this.searchError.set(error.message || 'Search failed');
      this.isSearching.set(false);

      const emptyResult: SearchResult = {
        products: [],
        totalFound: 0,
        facets: [],
        searchTime: 0
      };

      this.searchResults.set(emptyResult);
      return emptyResult;
    }
  }

  buildFilterString(filters: {
    categories?: string[];
    subcategories?: string[];
    producers?: string[];
    tags?: string[];
    isOnSale?: boolean;
    priceMin?: number;
    priceMax?: number;
  }): string {
    const filterParts: string[] = [];

    if (filters.categories?.length) {
      filterParts.push(`category_name:[${filters.categories.map(c => `\`${c}\``).join(',')}]`);
    }

    if (filters.subcategories?.length) {
      filterParts.push(`subcategory_name:[${filters.subcategories.map(s => `\`${s}\``).join(',')}]`);
    }

    if (filters.producers?.length) {
      filterParts.push(`producer_name:[${filters.producers.map(p => `\`${p}\``).join(',')}]`);
    }

    if (filters.tags?.length) {
      filterParts.push(`tag_names:[${filters.tags.map(t => `\`${t}\``).join(',')}]`);
    }

    if (filters.isOnSale !== undefined) {
      filterParts.push(`is_on_sale:=${filters.isOnSale}`);
    }

    if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
      const min = filters.priceMin ?? 0;
      const max = filters.priceMax ?? 999999;
      filterParts.push(`price:[${min}..${max}]`);
    }

    return filterParts.join(' && ');
  }

  /**
   * Fetches popular search terms from the Typesense Analytics query suggestions collection.
   * Falls back to subcategory facets if no analytics data is available yet (cold start).
   */
  async getPopularSearches(limit: number = 6): Promise<string[]> {
    try {
      const searches = await this.fetchQuerySuggestions(limit);
      if (searches.length > 0) {
        this.popularSearches.set(searches);
        return searches;
      }
      return this.fetchPopularFromFacets(limit);
    } catch {
      return this.fetchPopularFromFacets(limit);
    }
  }

  private async fetchQuerySuggestions(limit: number): Promise<string[]> {
    const result = await this.client
      .collections(this.querySuggestionsCollection)
      .documents()
      .search({
        q: '*',
        query_by: 'q',
        sort_by: 'count:desc',
        per_page: limit
      } as any);

    const raw = (result.hits || [])
      .map((hit: any) => (hit.document.q as string).replace(/[^a-zA-Z0-9\s]/g, '').trim().toLowerCase())
      .filter((q: string) => q && q !== '*' && q.length >= 3);

    // Deduplicate: remove entries that are a prefix/substring of another (higher-count) entry
    const deduped: string[] = [];
    for (const term of raw) {
      const isDuplicate = deduped.some(existing =>
        existing.includes(term) || term.includes(existing)
      );
      if (!isDuplicate) deduped.push(term);
    }

    return deduped;
  }

  private async fetchPopularFromFacets(limit: number): Promise<string[]> {
    try {
      const result = await this.client
        .collections(this.collection)
        .documents()
        .search({
          q: '*',
          query_by: 'name',
          per_page: 0,
          facet_by: 'subcategory_name',
          max_facet_values: limit
        });

      const facet = (result.facet_counts || [])
        .find((f: any) => f.field_name === 'subcategory_name');

      const searches = (facet?.counts || [])
        .sort((a: any, b: any) => b.count - a.count)
        .slice(0, limit)
        .map((c: any) => c.value as string);

      this.popularSearches.set(searches);
      return searches;
    } catch {
      this.popularSearches.set([]);
      return [];
    }
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.searchResults.set(null);
    this.suggestions.set([]);
    this.searchError.set(null);
  }

  clearSuggestions(): void {
    this.suggestions.set([]);
    this.querySuggestions.set([]);
    this.suggestionsTotalFound.set(0);
    this.suggestionsReturnedCount.set(0);
  }

  // Word-position-aware name match. Lower tier = better rank.
  //   0       — name starts with the query ("Milk Chocolate" for "milk").
  //   N (1+)  — query is the prefix of the Nth word ("Chocolate Milk" → 2,
  //             "Cookies And Cream Milk" → 4).
  //   100     — query is a substring inside a word but not at its start.
  //   200     — query not present in the name (match came from another field).
  // Stable within tiers so Typesense's relevance order is preserved as a tiebreaker.
  private rerankByNameMatch<T extends { name?: string }>(items: T[], query: string): T[] {
    const q = (query || '').trim().toLowerCase();
    if (!q) return items;

    const tier = (item: T): number => {
      const name = (item.name || '').toLowerCase().trim();
      if (!name) return 200;
      if (name.startsWith(q)) return 0;

      const words = name.split(/[\s\-:,.()/]+/).filter(Boolean);
      const wordIdx = words.findIndex(w => w.startsWith(q));
      if (wordIdx >= 0) return wordIdx + 1;

      if (name.includes(q)) return 100;
      return 200;
    };

    return items
      .map((item, idx) => ({ item, idx, t: tier(item) }))
      .sort((a, b) => a.t - b.t || a.idx - b.idx)
      .map(x => x.item);
  }

  /**
   * Hydrates recentSearches from localStorage. Safe to call repeatedly;
   * subsequent calls are no-ops if already loaded.
   */
  loadRecentSearches(): void {
    if (this.recentSearches().length > 0) return;
    const stored = this.localStorage.get<string[]>(RECENT_SEARCHES_STORAGE_KEY);
    if (Array.isArray(stored)) this.recentSearches.set(stored);
  }

  addRecentSearch(query: string): void {
    const q = (query || '').trim();
    if (!q) return;
    // Case-insensitive dedupe; most-recent first; cap at RECENT_SEARCHES_LIMIT.
    const next = [q, ...this.recentSearches().filter(x => x.toLowerCase() !== q.toLowerCase())]
      .slice(0, RECENT_SEARCHES_LIMIT);
    this.recentSearches.set(next);
    this.localStorage.set(RECENT_SEARCHES_STORAGE_KEY, next);
  }

  removeRecentSearch(query: string): void {
    const next = this.recentSearches().filter(x => x !== query);
    this.recentSearches.set(next);
    this.localStorage.set(RECENT_SEARCHES_STORAGE_KEY, next);
  }

  clearRecentSearches(): void {
    this.recentSearches.set([]);
    this.localStorage.remove(RECENT_SEARCHES_STORAGE_KEY);
  }

  /**
   * Fetches popular/featured products sorted by position.
   * Used when search bar is focused with empty query.
   */
  async getPopularProducts(limit: number = 8): Promise<SearchSuggestion[]> {
    try {
      const searchParameters: Record<string, string> = {
        q: '*',
        query_by: 'name',
        per_page: String(limit),
        sort_by: 'position:asc'
      };

      const result = await this.client
        .collections(this.collection)
        .documents()
        .search(searchParameters);

      const products: SearchSuggestion[] = (result.hits || []).map((hit: any) => {
        const doc = hit.document as TypesenseDocument;
        const richProduct = this.parseProductData(doc);

        return {
          id: doc.id,
          name: doc.name,
          type: 'product' as const,
          img: doc.img,
          productUrl: doc.product_url,
          price: doc.price,
          originalPrice: doc.original_price,
          producerName: doc.producer_name,
          isSubscription: doc.is_subscription,
          isALaCarte: doc.is_a_la_carte,
          isBundle: richProduct?.isBundle ?? false,
          priceRange: richProduct?.priceRange,
          packages: richProduct?.packages,
          attributes: richProduct?.attributes,
          canQuickAdd: richProduct
            ? isSimpleOneTimeProduct(richProduct)
            : isSimpleOneTimeProduct({
                isALaCarte: doc.is_a_la_carte,
                isSubscription: doc.is_subscription,
                isBundle: false,
                attributes: [],
                packages: []
              }),
          stockInfo: richProduct?.stockInfo
        };
      });

      const expanded = attachStockState(expandSuggestions(products));
      this.popularProducts.set(expanded);
      return expanded;
    } catch (error) {
      this.popularProducts.set([]);
      return [];
    }
  }

  /**
   * Generates query suggestions from product names matching the search query.
   */
  private generateQuerySuggestions(query: string, hits: any[]): string[] {
    if (!query || hits.length === 0) return [];

    const queryLower = query.toLowerCase();
    const suggestions = new Set<string>();

    suggestions.add(query.toLowerCase());

    hits.forEach((hit: any) => {
      const doc = hit.document as TypesenseDocument;
      const nameLower = doc.name.toLowerCase();

      const words = nameLower.split(/\s+/);
      const queryWordIndex = words.findIndex(word => word.startsWith(queryLower) || word.includes(queryLower));

      if (queryWordIndex >= 0) {
        suggestions.add(words[queryWordIndex]);

        if (queryWordIndex + 1 < words.length) {
          suggestions.add(`${words[queryWordIndex]} ${words[queryWordIndex + 1]}`);
        }
        if (queryWordIndex > 0) {
          suggestions.add(`${words[queryWordIndex - 1]} ${words[queryWordIndex]}`);
        }
      }
    });

    return Array.from(suggestions)
      .filter(s => s.length >= query.length)
      .slice(0, 6);
  }

  /**
   * Parses the `product_data` JSON string from a Typesense document.
   * Returns null if the field is missing or invalid.
   */
  private parseProductData(doc: TypesenseDocument): TypesenseProduct | null {
    if (!doc.product_data) return null;
    try {
      return JSON.parse(doc.product_data) as TypesenseProduct;
    } catch {
      return null;
    }
  }

  private transformToProductResume(hits: any[]): ProductResume[] {
    return hits.map((hit: any) => {
      const doc = hit.document as TypesenseDocument;
      const richProduct = this.parseProductData(doc);

      if (richProduct) {
        return {
          id: richProduct.id,
          name: richProduct.name,
          price: richProduct.price,
          originalPrice: richProduct.originalPrice,
          productUrl: richProduct.productUrl,
          category: richProduct.category,
          subcategory: richProduct.subcategory,
          subcategorySon: richProduct.subcategorySon ?? { id: 0, name: '' },
          img: richProduct.img,
          hrImg: '',
          isALaCarte: richProduct.isALaCarte,
          isSubscription: richProduct.isSubscription,
          tags: richProduct.tags as any,
          attributes: richProduct.attributes,
          packages: richProduct.packages,
          display: richProduct.display,
          producer: richProduct.producer,
          categoryInView: null,
          subCategoryInView: null,
          subCategorySonInView: null,
          categoryAnchorLink: '',
          subcategoryAnchorLink: '',
          subcategorySonAnchorLink: ''
        };
      }

      // Fallback: build from flat fields if product_data is not available
      return {
        id: parseInt(doc.id, 10),
        name: doc.name,
        price: doc.price,
        originalPrice: doc.original_price,
        productUrl: doc.product_url,
        category: {
          id: parseInt(doc.category_id, 10),
          name: doc.category_name
        },
        subcategory: {
          id: parseInt(doc.subcategory_id, 10),
          name: doc.subcategory_name
        },
        subcategorySon: { id: 0, name: '' },
        img: doc.img,
        hrImg: '',
        isALaCarte: doc.is_a_la_carte,
        isSubscription: doc.is_subscription,
        tags: doc.tag_names?.map((name, index) => ({
          id: doc.tag_ids?.[index] ? parseInt(doc.tag_ids[index], 10) : index,
          name,
          icon: ''
        })) as any,
        producer: doc.producer_id ? {
          id: parseInt(doc.producer_id, 10),
          name: doc.producer_name || ''
        } : undefined,
        categoryInView: null,
        subCategoryInView: null,
        subCategorySonInView: null,
        categoryAnchorLink: '',
        subcategoryAnchorLink: '',
        subcategorySonAnchorLink: ''
      };
    });
  }

  private transformFacets(facetCounts: any[]): SearchFacet[] {
    return facetCounts.map((facet: any) => ({
      field: facet.field_name,
      counts: facet.counts.map((count: any) => ({
        value: count.value,
        count: count.count
      }))
    }));
  }
}
